// System
import React, { useEffect, useMemo } from 'react';

// Components
import { TablePagination } from '@mui/material';
import { TableBodyEx, TableContainerEx, TableEx } from '@Components/shared/tables/components';
import {
  ColumnVisibilityPresenter,
  TableHeadColumnsPresenter,
  TableHeadPresenter,
  TableRowPresenter,
} from '@Components/shared/tables/presenters';
import { LoadingScreen } from '@Components/shared/loaders/LoadingScreen';

// Utils
import { applyColumnVisibility } from '@utils/data/applyColumnVisibility';

//  Table
import { useMinesRoundsTableColumns } from './useMinesRoundsTableColumns';

// Redux
import { useDispatch } from 'react-redux';
import { MinesRoundDto } from '@Modal/reducers/rounds/types';
import { useMinesRoundsTable } from '@Modal/hooks/rounds/useMinesRoundsTable';

// Graphql
import { usePaginatedMinesRoundsQuery } from '@Src/generated.graphql';

// Hooks
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';
import { useMinesRoundDrawer } from '@Modal/hooks/rounds/useMinesRoundDrawer';

export const MinesRoundsTable: React.FC = () => {
  const dispatch = useDispatch();
  const {
    roundsListFilters,
    columnVisibility,
    visiblityCustomizable,
    limit,
    total,
    page,
    setLimit,
    setPage,
    setTotal,
    roundTableFilters,
  } = useMinesRoundsTable();
  const columns = useMinesRoundsTableColumns();
  const { handleRoundRowClick } = useMinesRoundDrawer();
  const { userPermissions, currentUser } = useAuthorization();
  const permissionToViewRounds =
    userPermissions?.includes('CAN_VIEW_MINES_ROUNDS') || currentUser?.isSuper;

  const { data, loading, refetch } = usePaginatedMinesRoundsQuery({
    variables: {
      limit,
      page,
      filter: roundTableFilters,
    },
    skip: !permissionToViewRounds,
  });

  const visibleColumns = useMemo(
    () => applyColumnVisibility(columns, roundsListFilters),
    [columns, columnVisibility]
  );

  useEffect(() => {
    if (data?.paginatedMinesRounds) {
      const { total, limit, page } = data.paginatedMinesRounds;
      dispatch(setTotal(total));
      dispatch(setLimit(limit));
      dispatch(setPage(page));
    }
  }, [data]);

  const header = useMemo(
    () => (
      <TableHeadPresenter>
        <TableHeadColumnsPresenter columns={visibleColumns} />
        {/* @ts-ignore */}
        <ColumnVisibilityPresenter columns={columns} content={visiblityCustomizable} />
      </TableHeadPresenter>
    ),
    [columns]
  );

  if (!permissionToViewRounds) return <h1>No Permission</h1>;
  if (loading) return <LoadingScreen />;

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    refetch();
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setLimit(parseInt(event.target.value, 10)));
    dispatch(setPage(1));
    refetch();
  };

  return (
    <>
      <TableContainerEx sx={{ height: '70vh' }} pageNumber={1}>
        <TableEx name="RoundsList">
          {header}
          <TableBodyEx>
            {/* @ts-ignore */}
            {data?.paginatedMinesRounds?.docs.map((row: MinesRoundDto) => (
              <TableRowPresenter
                id={row._id}
                key={row._id}
                columns={visibleColumns}
                row={row}
                onClick={() => handleRoundRowClick(row.roundId)}
              />
            ))}
          </TableBodyEx>
        </TableEx>
      </TableContainerEx>
      <TableEx name="CustomerListFooter">
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableEx>
    </>
  );
};
